import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Loader2,
  Package,
  Plus,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { ViewToggleButton } from "@/components/ui/ViewToggleButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { RowActions } from "@/components/ui/RowActions";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { initials } from "@/lib/utils";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList, useDeleteProduct } from "../hooks/useProducts";
import { NewProductDrawer } from "../components/NewProductDrawer";
import { useViewMode } from "@/hooks/useViewMode";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import type { ProductListItem, ProductStatus } from "../types";

type StatusFilter = "" | ProductStatus;

const PRODUCT_COLUMNS: ColumnDef[] = [
  { key: "name", label: "Name + Code" },
  { key: "companyName", label: "Company" },
  { key: "status", label: "Status" },
  { key: "projects", label: "Projects" },
];

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    ACTIVE: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)", label: "Active" },
    INACTIVE: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)", label: "Inactive" },
    ARCHIVED: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)", label: "Archived" },
  };
  const s = styles[status] ?? styles.INACTIVE;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function ProductRow({
  product,
  onDelete,
  isVisible,
}: {
  product: ProductListItem;
  onDelete: (id: string, name: string) => void;
  isVisible: (key: string) => boolean;
}) {
  const navigate = useNavigate();
  return (
    <tr
      className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
      onClick={() => navigate(`/products/${product.id}`)}
    >
      {isVisible("name") && (
        <td className="px-6 py-4">
          <div className="font-medium text-foreground">{product.name}</div>
          <div className="mt-0.5 font-mono text-xs text-muted-foreground">
            {product.code}
          </div>
        </td>
      )}
      {isVisible("companyName") && (
        <td className="px-6 py-4">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Building2 className="h-3 w-3" strokeWidth={1.5} />
            {product.companyName}
          </div>
        </td>
      )}
      {isVisible("status") && (
        <td className="px-6 py-4">
          <StatusBadge status={product.status} />
        </td>
      )}
      {isVisible("projects") && (
        <td className="px-6 py-4 text-sm text-muted-foreground">
          {product._count.projects}
        </td>
      )}
      <td className="px-6 py-4">
        <RowActions
          onView={() => navigate(`/products/${product.id}`)}
          onEdit={() => navigate(`/products/${product.id}?edit=true`)}
          onDelete={() => onDelete(product.id, product.name)}
        />
      </td>
    </tr>
  );
}

function ProductCard({ product }: { product: ProductListItem }) {
  const navigate = useNavigate();
  return (
    <div
      className="cursor-pointer rounded-xl border bg-card p-5 transition-colors hover:bg-[hsl(210,50%,98%)]"
      style={{ borderColor: "hsl(210,30%,85%)" }}
      onClick={() => navigate(`/products/${product.id}`)}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(210,55%,92%)] text-sm font-semibold text-[hsl(210,100%,28%)]">
          {initials(product.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">{product.name}</div>
          <div className="mt-0.5 font-mono text-xs text-muted-foreground">
            {product.code}
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Building2 className="h-3 w-3" strokeWidth={1.5} />
        {product.companyName}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <StatusBadge status={product.status} />
      </div>
      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
        <span>
          <span className="font-medium text-foreground">
            {product._count.projects}
          </span>{" "}
          Projects
        </span>
      </div>
    </div>
  );
}

export default function ProductListPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const { mode: viewMode, toggle: toggleViewMode, isGrid } = useViewMode(
    "products",
    "list",
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("products", PRODUCT_COLUMNS);
  const exportFilters = useMemo(
    () => ({
      search: search.trim() || undefined,
      status: statusFilter || undefined,
      companyId: companyFilter || undefined,
    }),
    [search, statusFilter, companyFilter],
  );

  const deleteProduct = useDeleteProduct();

  const { data: companiesResult } = useCompanyList({ page: 1, limit: 100, operationalOnly: true });
  const allCompanies = companiesResult?.data ?? [];

  const {
    data: result,
    isLoading,
    isError,
    error,
  } = useProductList({
    page,
    limit,
    search: search.trim() || undefined,
    status: statusFilter || undefined,
    companyId: companyFilter || undefined,
  });

  const products = result?.data ?? [];
  const pagination = result?.pagination ?? {
    page: 1,
    limit,
    total: 0,
    totalPages: 1,
  };
  const total = pagination.total;

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function handleDelete(id: string, name: string) {
    setDeleteTarget({ id, name });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteProduct.mutateAsync(deleteTarget.id);
    showToast(`${deleteTarget.name} deleted successfully`);
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Business Structure" },
        { label: "Products" },
      ]}
    >
      <div className="w-full space-y-6">
        {/* Toast */}
        {toast && (
          <div
            className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
            style={{ borderColor: "hsl(142,55%,80%)" }}
          >
            {toast}
          </div>
        )}

        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">Products</h1>
          </div>
          <div className="flex items-center gap-2">
            <ViewToggleButton mode={viewMode} onToggle={toggleViewMode} />
            <ExportButton
              module="products"
              filters={exportFilters}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={PRODUCT_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Product
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load products</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            {/* Filters */}
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px]">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) =>
                  resetPage(setStatusFilter)(e.target.value as StatusFilter)
                }
                className="w-40"
              >
                <option value="">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="ARCHIVED">Archived</option>
              </Select>
              <Select
                value={companyFilter}
                onChange={(e) =>
                  resetPage(setCompanyFilter)(e.target.value)
                }
                className="w-48"
              >
                <option value="">All Companies</option>
                {allCompanies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Grid view */}
            {isGrid ? (
              <div>
                {isLoading ? (
                  <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      strokeWidth={1.5}
                    />
                    Loading products…
                  </div>
                ) : products.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground">
                    No products match the current filters.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {products.map((p) => (
                      <ProductCard key={p.id} product={p} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Table view */
              <div
                className="overflow-hidden rounded-xl border bg-card"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                    <Loader2
                      className="mr-2 h-4 w-4 animate-spin"
                      strokeWidth={1.5}
                    />
                    Loading products…
                  </div>
                ) : products.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground">
                    No products match the current filters.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <tr>
                        {isVisible("name") && (
                          <th className="px-6 py-3">Name + Code</th>
                        )}
                        {isVisible("companyName") && (
                          <th className="px-6 py-3">Company</th>
                        )}
                        {isVisible("status") && (
                          <th className="px-6 py-3">Status</th>
                        )}
                        {isVisible("projects") && (
                          <th className="px-6 py-3">Projects</th>
                        )}
                        <th className="px-6 py-3 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {products.map((p) => (
                        <ProductRow
                          key={p.id}
                          product={p}
                          onDelete={handleDelete}
                          isVisible={isVisible}
                        />
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Pagination footer */}
                <div data-pagination="true" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '9px 14px',
                  borderTop: '1px solid hsl(210,30%,85%)',
                  background: 'hsl(210,30%,97%)',
                  fontSize: '12px',
                  color: 'hsl(215,16%,47%)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>
                      Showing {total === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total} entries
                    </span>
                    <span style={{ marginLeft: '8px' }}>Show</span>
                    <select
                      value={limit}
                      onChange={e => { setLimit(Number(e.target.value)); setPage(1) }}
                      style={{
                        height: '26px',
                        padding: '0 4px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: 'white',
                        cursor: 'pointer',
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span>entries</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button
                      onClick={() => setPage(p => p - 1)}
                      disabled={page === 1}
                      style={{
                        width: '28px', height: '28px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: page === 1 ? 'not-allowed' : 'pointer',
                        opacity: page === 1 ? 0.4 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >‹</button>
                    <span style={{ padding: '0 10px' }}>
                      {Math.ceil(total / limit) === 0 ? '0' : page} / {Math.ceil(total / limit) || 1}
                    </span>
                    <button
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= Math.ceil(total / limit)}
                      style={{
                        width: '28px', height: '28px',
                        border: '1px solid hsl(210,30%,85%)',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                        opacity: page >= Math.ceil(total / limit) ? 0.4 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >›</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <NewProductDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />

        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete product?"
          description={
            deleteTarget
              ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Products list.`
              : ""
          }
          onConfirm={confirmDelete}
        />
      </div>
    </AppLayout>
  );
}
